import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Siren, HeartHandshake, CalendarCheck, Stethoscope, FileText, HelpCircle, CreditCard, ChevronRight, Phone, MessageCircle } from "lucide-react";
import api, { fmtDay, fmtTime, formatErr } from "../../lib/api";
import { Loading, StatusPill } from "../../components/Shared";
import { AppHeader } from "../../components/native/AppShell";
import { useAuth } from "../../context/AuthContext";

const STATUS_STYLE = {
  attention: "bg-red-50 border-red-300 text-red-800",
  well: "bg-emerald-50 border-emerald-300 text-emerald-900",
  neutral: "bg-stone-50 border-stone-200 text-stone-600",
};

function HomeCard({ icon: Icon, title, children, to, testid, accent }) {
  const inner = (
    <div className="p1-card flex items-center gap-3 p-4 transition-shadow active:shadow-md" data-testid={testid}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent || "bg-emerald-50 text-emerald-900"}`}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-wide text-stone-400">{title}</p>
        <div className="text-sm font-semibold text-stone-800">{children}</div>
      </div>
      {to && <ChevronRight className="h-4 w-4 shrink-0 text-stone-300" />}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function FamilyHome() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [parentId, setParentId] = useState(null);
  useEffect(() => {
    api.get("/family/overview").then((r) => { setD(r.data); setParentId(r.data.parents?.[0]?.id || null); })
      .catch((e) => { formatErr(e); setD(e?.response?.status === 404 ? { needs_setup: true } : { empty: true }); });
  }, []);
  if (!d) return <div className="min-h-screen bg-sand"><AppHeader title="ParentOne Care" /><Loading /></div>;

  if (d.needs_setup) {
    return (
      <div data-testid="native-family-setup-needed">
        <AppHeader title="ParentOne Care" />
        <main className="mx-auto max-w-lg px-4 py-8">
          <div className="p1-card p-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-900"><HeartHandshake className="h-7 w-7" /></div>
            <h1 className="mt-4 font-heading text-xl font-bold text-stone-900">Complete your family setup</h1>
            <p className="mt-2 text-sm text-stone-600">
              Tell us about your parent and your care needs — it takes about 2 minutes.
              You can leave anytime and resume later; your progress is saved on this device.
            </p>
            <Link to="/onboarding" className="p1-btn-primary mt-5 flex w-full justify-center" data-testid="setup-start-btn">Complete Family Setup</Link>
            <p className="mt-3 text-[11px] text-stone-400">Your care dashboard activates as soon as setup is submitted.</p>
          </div>
          <a href="tel:+918690545601" className="mt-4 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800" data-testid="setup-sos-link">
            <Siren className="h-5 w-5 shrink-0" />
            <span className="text-xs font-semibold">Need help right now? Call the ParentOne care team.</span>
          </a>
        </main>
      </div>
    );
  }

  const parents = d.parents || [];
  const activeParent = parents.find((p) => p.id === parentId) || parents[0];
  const cm = d.care_manager || {};
  const cmPhone = (cm.work_phone || cm.phone || "").replace(/\s/g, "");
  const status = d.status || { state: "neutral", message: "" };

  return (
    <div data-testid="native-family-home">
      <AppHeader title="ParentOne Care" subtitle={d.family?.name} />
      <main className="mx-auto max-w-lg space-y-3.5 px-4 py-5">
        <div>
          <h1 className="font-heading text-xl font-bold text-stone-900" data-testid="home-greeting">
            Namaste, {(user?.name || "").split(" ")[0]}
          </h1>
          <p className="text-xs text-stone-500">Here is your parents' care at a glance.</p>
        </div>

        {parents.length > 1 && (
          <div className="flex gap-2" data-testid="home-parent-selector">
            {parents.map((p) => (
              <button key={p.id} onClick={() => setParentId(p.id)} data-testid={`home-parent-${p.id}`}
                className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${p.id === (activeParent?.id) ? "bg-emerald-900 text-white" : "border border-stone-200 bg-white text-stone-500"}`}>
                {p.name}
              </button>
            ))}
          </div>
        )}

        <div className={`rounded-2xl border p-4 text-sm font-semibold ${STATUS_STYLE[status.state] || STATUS_STYLE.neutral}`} data-testid="home-status-banner">
          {activeParent ? status.message : "Welcome to ParentOne Care."}
        </div>

        <a href="tel:+918690545601" className="flex items-center justify-between rounded-2xl bg-red-600 p-4 text-white shadow-lg transition-transform active:scale-[0.98]" data-testid="home-sos-card">
          <div className="flex items-center gap-3">
            <Siren className="h-7 w-7" />
            <div>
              <p className="text-base font-extrabold">Emergency / SOS</p>
              <p className="text-xs font-medium opacity-90">Tap to call the ParentOne care team now</p>
            </div>
          </div>
          <Phone className="h-5 w-5" />
        </a>

        <HomeCard icon={HeartHandshake} title="Your Care Manager" testid="home-cm-card" to="/app/family/care">
          {cm.name ? (
            <span className="flex items-center gap-2">{cm.name}
              {cmPhone && <span className="flex gap-1">
                <span onClick={(e) => { e.preventDefault(); window.location.href = `tel:${cmPhone}`; }} className="rounded-full bg-emerald-900 p-1.5 text-white" data-testid="home-cm-call"><Phone className="h-3 w-3" /></span>
                <span onClick={(e) => { e.preventDefault(); window.open(`https://wa.me/${cmPhone.replace(/\D/g, "")}`); }} className="rounded-full border border-emerald-800 p-1.5 text-emerald-900" data-testid="home-cm-wa"><MessageCircle className="h-3 w-3" /></span>
              </span>}
            </span>
          ) : "Being assigned — we will notify you"}
        </HomeCard>

        <HomeCard icon={CalendarCheck} title="Next visit" testid="home-next-visit" to="/app/family/calendar" accent="bg-blue-50 text-blue-900">
          {d.next_visit ? <>{fmtDay(d.next_visit.scheduled_at)} · {fmtTime(d.next_visit.scheduled_at)}<p className="text-xs font-medium text-stone-500">{d.next_visit.purpose || "Care visit"}</p></> : "No visit scheduled yet"}
        </HomeCard>

        {d.upcoming_appointment && (
          <HomeCard icon={Stethoscope} title="Upcoming appointment" testid="home-appointment" to="/app/family/calendar" accent="bg-violet-50 text-violet-900">
            {fmtDay(d.upcoming_appointment.when)}{d.upcoming_appointment.title ? ` · ${d.upcoming_appointment.title}` : ""}
          </HomeCard>
        )}

        <HomeCard icon={FileText} title="Latest care update" testid="home-latest-update" to="/app/family/visits" accent="bg-amber-50 text-amber-900">
          {d.latest_report ? <>Visit report · {fmtDay(d.latest_report.scheduled_at)}</> : (d.recent_activity || [])[0]?.title || "Updates will appear after the first visit"}
        </HomeCard>

        {(d.open_requests || []).length > 0 && (
          <HomeCard icon={HelpCircle} title="Open service request" testid="home-open-request" to="/app/family/requests" accent="bg-orange-50 text-orange-900">
            <span className="flex items-center gap-2">{d.open_requests[0].type} <StatusPill status={d.open_requests[0].status} /></span>
          </HomeCard>
        )}

        {d.plan && (
          <HomeCard icon={CreditCard} title="Your plan" testid="home-plan" to="/app/family/billing" accent="bg-stone-100 text-stone-700">
            {d.plan.name}{d.subscription?.renews_at ? ` · renews ${fmtDay(d.subscription.renews_at)}` : ""}
          </HomeCard>
        )}
      </main>
    </div>
  );
}
